import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../../../redux/TranslationHelper';
import YooptaHtmlEditor from '../../common/YooptaEditor';
import { yooptaToBackend } from '../../../utils/YooptaDataConverter';

import { FormData, CommentData, Post } from './Board.types';
import { adaptBoardProps } from './Board.adapter';
import './Board.css'; // CSS 파일 임포트 추가

import { 
  fetchBoards, 
  fetchPosts, 
  fetchPostDetail, 
  createPost, 
  updatePost, 
  deletePost, 
  createComment,
  selectBoards,
  selectPosts, 
  selectPostsLoading, 
  selectPostDetail, 
  selectPostDetailLoading,
  selectPostsPagination,
  selectUser
} from '../../../redux/WithcookieSlice';

/**
 * 게시판 컴포넌트
 * 게시판 목록, 게시글 작성, 수정, 상세보기 기능을 제공합니다.
 */
const BoardComponent: React.FC<any> = (props) => {
  const { fetchBoardsOnce, fetchPostsOnce, isPreview = false } = props;
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptBoardProps(props);
  const { style = {}, data = {}} = adaptedProps;
  

  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { target } = useParams(); // URL 파라미터 추출
  
  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();
  
  // 게시판, 게시글, 모드 관련 상태 관리
  const [mode, setMode] = useState<'list' | 'detail' | 'new' | 'edit'>('list');
  const [boardCode, setBoardCode] = useState<string>('');
  const [postId, setPostId] = useState<string | number | null>(null);
  const [selectedBoardId, setSelectedBoardId] = useState<string | number | null>(null);
  const [formData, setFormData] = useState<FormData>({ title: '', content: '', originalBlock: null });
  const [commentData, setCommentData] = useState<CommentData>({ content: '' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  
  // 리덕스 상태 가져오기 (any 타입 사용 - 실제 구현시 정확한 타입으로 변경 필요)
  const boards = useSelector(selectBoards);
  const posts = useSelector(selectPosts);
  const postsLoading = useSelector(selectPostsLoading);
  const postDetail = useSelector(selectPostDetail);
  const postDetailLoading = useSelector(selectPostDetailLoading);
  const pagination = useSelector(selectPostsPagination);
  
  // 현재 로그인한 사용자 정보 가져오기
  const currentUser = useSelector(selectUser);
  
  // 현재 디바이스 감지 (모바일 또는 PC)
  const [currentDevice, setCurrentDevice] = useState<'mobile' | 'pc'>(() => {
    // 초기값을 함수로 제공하여 컴포넌트 마운트 전에 한 번만 실행
    return window.innerWidth <= 768 ? 'mobile' : 'pc';
  });
  
  // forceUpdate 상태 제거 - 불필요한 렌더링 방지
  
  // 초기 렌더링 플래그 관리
  const initialRenderRef = useRef<boolean>(true);
  const styleProcessedRef = useRef<boolean>(false);
  const loadedRef = useRef<boolean>(false); // 중복 로드 방지용 플래그 추가
  const urlParsingRef = useRef<boolean>(false);

  /* ------------------- useEffect & useLayoutEffect 구현 ------------------- */

  // 수정 모드에서 기존 게시글 데이터 불러오기
  useEffect(() => {
    

    if (mode === 'edit' && postDetail) {
      const post = postDetail.post || postDetail;
      
      // 기존 블럭 데이터가 있으면 우선 사용, 없으면 HTML 사용
      let initialContent = post.content || '';
      let initialOriginalBlock = post.originalBlock || null;
      
     
      
      setFormData({
        title: post.title || '',
        content: initialContent,
        originalBlock: initialOriginalBlock
      });
    }
  }, [mode, postDetail]);

  // 에디터 초기 데이터 - 한 번만 계산되도록 안정화
  const initialDataRef = useRef('');
  const [editorInitialData, setEditorInitialData] = useState('');
  
  // mode나 postDetail이 변경될 때만 초기 데이터 업데이트
  useEffect(() => {
    if (mode === 'edit' && postDetail) {
      const post = postDetail.post || postDetail;
      const newInitialData = post.content || '';
      
      if (initialDataRef.current !== newInitialData) {
       
        initialDataRef.current = newInitialData;
        setEditorInitialData(newInitialData);
      }
    } else if (mode === 'new') {
      if (initialDataRef.current !== '') {
       
        initialDataRef.current = '';
        setEditorInitialData('');
      }
    }
  }, [mode, postDetail]);

  // 글로벌 이벤트 트리거 제거 - 불필요한 렌더링 방지
  
  // 화면 크기에 따라 디바이스 타입 결정 (최우선 실행)
  useLayoutEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      const newDevice = isMobile ? 'mobile' : 'pc';
      
      if (newDevice !== currentDevice) {
        setCurrentDevice(newDevice);
        // 디바이스 타입이 변경된 경우 처리
      }
    };
    
    // 초기 실행
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // 의존성 배열을 비워서 마운트 시 한 번만 실행
  
  // 스타일 초기화 useLayoutEffect - 타이머 제거
  useLayoutEffect(() => {
    // 이미 처리된 경우 중복 실행 방지
    if (styleProcessedRef.current) {
      return;
    }
    
    // 스타일 처리 완료 표시
    styleProcessedRef.current = true;
  }, []); // 빈 의존성 배열로 마운트 시 한 번만 실행

  // 스타일 변경 감지 및 적용 useLayoutEffect 제거
  // 초기 렌더링 시에만 스타일을 적용하고 이후에는 변경하지 않음

  // 현재 디바이스에 맞는 설정값 가져오기
  const getDeviceValue = (field: string, defaultValue: any): any => {
    // 기본 검증
    if (!data || Array.isArray(data)) {
      return defaultValue;
    }
    
    try {
      // 1. 현재 디바이스 설정 확인 (최우선)
      if (data.device && 
          data.device[currentDevice] && 
          data.device[currentDevice]?.[field] !== undefined) {
        
        const value = data.device[currentDevice]?.[field];
        
        // title 필드인 경우 다국어 처리 적용
        if (field === 'title' && value) {
          return t(value);
        }
        
        return value;
      }
      
      // 2. 공통 설정 확인
      if (data[field] !== undefined) {
        const value = data[field];
        
        // title 필드인 경우 다국어 처리 적용
        if (field === 'title' && value) {
          return t(value);
        }
        
        return value;
      }
      
      return defaultValue;
    } catch (error) {
      console.error(`[BoardComponent] 데이터 처리 오류:`, error);
      return defaultValue;
    }
  };
  
  // 게시판 파라미터 가져오기
  let boardParameter = getDeviceValue('parameter', '');
  
  // 스타일 적용 함수 단순화 및 메모이제이션
  const getSimpleStyle = useCallback((styleKey: string, defaultValue: string): string => {
    // 디바이스별 스타일 먼저 확인
    if (style?.deviceProperty && style.deviceProperty[currentDevice]) {
      const deviceStyles = style.deviceProperty[currentDevice] as any;
      if (deviceStyles && deviceStyles[styleKey]) {
        return deviceStyles[styleKey] as string;
      }
    }
    // 기본 스타일 확인
    return style?.[styleKey as keyof typeof style] as string || defaultValue;
  }, [style, currentDevice]);

  // finapp 모드 먼저 확인 (computedStyle 계산 전에)
  const isFinapp = location.pathname.includes('/finapp/') || location.pathname.includes('/gpc/');

  // 스타일 계산을 강제 업데이트에 의존하지 않도록 변경
  const computedStyle = useMemo(() => {
    // finapp 모드일 때는 패딩을 0으로 설정
    if (isFinapp) {
      return {
        fontFamily: 'Inter, sans-serif',
        width: '100%',
        height: '100%',
        overflow: 'auto',
        paddingTop: '0px',
        paddingRight: '0px',
        paddingBottom: '0px',
        paddingLeft: '0px',
        backgroundColor: 'transparent',
        borderRadius: '0px',
        boxShadow: 'none',
        // 다른 스타일 속성 유지
        ...(Object.keys(style || {}).filter(key => 
          key !== 'device' && 
          !['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 
            'backgroundColor', 'borderRadius', 'boxShadow'].includes(key)
        ).reduce((acc: any, key: string) => {
          acc[key] = (style as any)[key];
          return acc;
        }, {}))
      };
    }
    
    // finapp 모드가 아닐 때는 원래대로
    const paddings = {
      paddingTop: getSimpleStyle('paddingTop', '0px'),
      paddingRight: getSimpleStyle('paddingRight', '0px'),
      paddingBottom: getSimpleStyle('paddingBottom', '0px'),
      paddingLeft: getSimpleStyle('paddingLeft', '0px')
    };
    
    const borderRadius = getSimpleStyle('borderRadius', '0px');
    
    // 스타일 객체 구성 - 패딩 값을 확실히 적용
    return {
      fontFamily: 'Inter, sans-serif',
      width: '100%',
      height: '100%',
      overflow: 'auto',
      paddingTop: paddings.paddingTop,
      paddingRight: paddings.paddingRight,
      paddingBottom: paddings.paddingBottom,
      paddingLeft: paddings.paddingLeft,
      backgroundColor: getSimpleStyle('backgroundColor', 'transparent'),
      borderRadius: borderRadius,
      boxShadow: getSimpleStyle('boxShadow', 'none'),
      // 다른 스타일 속성 유지
      ...(Object.keys(style || {}).filter(key => 
        key !== 'device' && 
        !['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 
          'backgroundColor', 'borderRadius', 'boxShadow'].includes(key)
      ).reduce((acc: any, key: string) => {
        acc[key] = (style as any)[key];
        return acc;
      }, {}))
    };
  }, [style, getSimpleStyle, isFinapp]); // isFinapp 의존성 추가
  
  // 게시판 파라미터 숫자로 변환 (API에서 category_id로 사용)
  let categoryId: number | null = null;
  if (boardParameter) {
    // 1. 순수한 숫자인 경우 바로 변환
    if (!isNaN(Number(boardParameter))) {
      categoryId = parseInt(boardParameter as string, 10);
    } 
    // 2. 문자열 내에 숫자가 있는 경우 (예: 'board1')
    else if (typeof boardParameter === 'string') {
      // 숫자만 추출하는 정규 표현식 사용
      const matches = boardParameter.match(/\d+/);
      if (matches && matches.length > 0) {
        categoryId = parseInt(matches[0], 10);
      }
    }
  }

  // 컴포넌트 초기화 시 게시판 목록 로드 - 최초 1회만
  useEffect(() => {
    // 이미 로드되었다면 다시 로드하지 않음
    if (loadedRef.current) return;
    
    // 최초 1회만 게시판 목록 로드
    if (fetchBoardsOnce) {
      fetchBoardsOnce();
    } else {
      (dispatch as any)(fetchBoards());
    }
    
    // 로드 완료 표시
    loadedRef.current = true;
  }, []); // 빈 의존성 배열 - 컴포넌트 마운트 시 1회만

  // parameter가 있을 때 게시글 목록 로드
  useEffect(() => {
    if (categoryId && !urlParsingRef.current) {
      if (fetchPostsOnce) {
        fetchPostsOnce(categoryId, 1, perPage);
      } else {
        (dispatch as any)(fetchPosts({
          categoryId: categoryId,
          page: 1,
          perPage
        }));
      }
      setCurrentPage(1);
      setSelectedBoardId(categoryId);
    }
  }, [categoryId, dispatch, perPage]); // categoryId가 설정될 때만 실행

  // 브라우저 뒤로가기/앞으로가기 감지 및 처리
  useEffect(() => {
    // 웹빌더 미리보기 모드에서는 popstate 이벤트 처리하지 않음
    if (isPreview) {
      return;
    }
    
    const handlePopState = () => {
      // 페이지를 다시 로드하여 상태를 갱신
      window.location.reload();
    };
    
    // popstate 이벤트 리스너 등록
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname, isPreview]);

  // 폼 데이터 변경 핸들러
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // 리치 에디터 내용 변경 핸들러 - 참조 안정화를 위해 useRef 사용
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  
  const handleEditorChange = useCallback((content: string, blocks: any) => {
    const newContent = content || '';
    const newOriginalBlock = blocks ? yooptaToBackend(blocks) : null;
    
    // 내용이 실제로 변경되었을 때만 업데이트
    if (formDataRef.current.content !== newContent) {
      setFormData(prev => ({ 
        ...prev, 
        content: newContent,  // HTML
        originalBlock: newOriginalBlock // 블럭 데이터
      }));
    }
  }, []); // 의존성 배열 완전히 비움

  // 댓글 입력 변경 핸들러
  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentData(prev => ({ ...prev, content: e.target.value }));
  }, []);

  // 게시글 저장 핸들러
  const handleSavePost = () => {

    
    if (!formData.title.trim()) {
      alert(t('제목을 입력해주세요.'));
      return;
    }

    if (!formData.content.trim()) {
      alert(t('내용을 입력해주세요.'));
      return;
    }
    
    if (mode === 'new') {
      // 백엔드 API 요청 시 항상 categoryId(parameter)를 우선 사용
      // parameter가 없는 경우에만 URL에서 추출한 ID 사용
      const boardId = categoryId || selectedBoardId;
      

      
      if (!boardId) {
        // boardCode가 있으면 boards에서 찾기
        if (boardCode && boards && boards.length > 0) {
          const foundBoard = boards.find((board: any) => board.code === boardCode);
          if (foundBoard) {

            const numericBoardId = Number(foundBoard.id);
            
            const postData = {
              category_id: numericBoardId,
              title: formData.title,
              content: formData.content,
              edit_type: 'WYSIWYG',
              ...(formData.originalBlock && { originalBlock: formData.originalBlock })
            };
            

            
            (dispatch as any)(createPost(postData))
              .unwrap()
              .then((response: any) => {

                const newPostId = response.post ? response.post.id : response.id;
                
                if (newPostId) {
                  alert(t('게시글이 성공적으로 등록되었습니다.'));
                  if (isPreview) {
                    // 웹빌더 미리보기 모드에서는 상세 모드로만 변경
                    setMode('detail');
                    setPostId(newPostId);
                    (dispatch as any)(fetchPostDetail(newPostId));
                  } else {
                    navigateToDetail(newPostId);
                  }
                } else {
                  alert(t('게시글이 등록되었지만 상세 보기로 이동할 수 없습니다. 목록으로 이동합니다.'));
                  if (isPreview) {
                    setMode('list');
                    // 게시글 목록 새로고침
                    if (categoryId || selectedBoardId) {
                      const boardId = categoryId || selectedBoardId;
                      const numericBoardId = boardId ? Number(boardId) : null;
                      
                      if (numericBoardId) {

                        if (fetchPostsOnce) {
                          fetchPostsOnce(numericBoardId, currentPage, perPage);
                        } else {
                          (dispatch as any)(fetchPosts({
                            categoryId: numericBoardId,
                            page: currentPage,
                            perPage
                          }));
                        }
                      }
                    }
                  } else {
                    navigateToList();
                  }
                }
              })
              .catch((error: any) => {
                console.error('[게시글 생성] 실패:', error);
                alert(t('게시글 등록에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
              });
            return;
          }
        }
        
        alert(t('게시판을 선택해주세요.'));
        return;
      }
      
      // 명시적으로 number 타입으로 변환
      const numericBoardId = Number(boardId);
      
      const postData = {
        category_id: numericBoardId, // API 요청 형식에 맞게 category_id로 설정
        title: formData.title,
        content: formData.content,
        edit_type: 'WYSIWYG',
        ...(formData.originalBlock && { originalBlock: formData.originalBlock })
      };
      

      
      (dispatch as any)(createPost(postData))
        .unwrap()
        .then((response: any) => {

          // 게시글 ID 추출 (응답 구조에 따라 조정 필요)
          const newPostId = response.post ? response.post.id : response.id;
          
          if (newPostId) {
            // 성공 메시지 표시 후 상세 페이지로 이동
            alert(t('게시글이 성공적으로 등록되었습니다.'));
            if (isPreview) {
              // 웹빌더 미리보기 모드에서는 상세 모드로만 변경
              setMode('detail');
              setPostId(newPostId);
              (dispatch as any)(fetchPostDetail(newPostId));
            } else {
              navigateToDetail(newPostId);
            }
          } else {
            // 응답에서 ID를 찾을 수 없는 경우 목록으로 이동
            alert(t('게시글이 등록되었지만 상세 보기로 이동할 수 없습니다. 목록으로 이동합니다.'));
            if (isPreview) {
              setMode('list');
              // 게시글 목록 새로고침
              if (categoryId || selectedBoardId) {
                const boardId = categoryId || selectedBoardId;
                const numericBoardId = boardId ? Number(boardId) : null;
                
                if (numericBoardId) {

                  if (fetchPostsOnce) {
                    fetchPostsOnce(numericBoardId, currentPage, perPage);
                  } else {
                    (dispatch as any)(fetchPosts({
                      categoryId: numericBoardId,
                      page: currentPage,
                      perPage
                    }));
                  }
                }
              }
            } else {
              navigateToList();
            }
          }
        })
        .catch((error: any) => {
          console.error('[게시글 생성] 실패:', error);
          alert(t('게시글 등록에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
        });
    } else if (mode === 'edit' && postDetail) {

      // API 응답 구조에 따라 게시글 ID 추출
      const post = postDetail.post || postDetail;
      const postId = post.id;
      
      const requestData = {
        title: formData.title,
        content: formData.content,
        ...(formData.originalBlock && { originalBlock: formData.originalBlock })
      };
      

      

      
      (dispatch as any)(updatePost({ postId, postData: requestData }))
        .unwrap()
        .then((response: any) => {
         
          
          alert(t('게시글이 성공적으로 수정되었습니다.'));
          setMode('detail');
          
          // 수정 후 게시글 상세 정보 다시 불러오기
          
          (dispatch as any)(fetchPostDetail(postId))
            .unwrap()
            .then((refreshedData: any) => {
             
            })
            .catch((fetchError: any) => {
              console.error('[Board] fetchPostDetail 실패:', fetchError);
            });
            
          if (!isPreview) {
            navigateToDetail(postId);
          }
        })
        .catch((error: any) => {
          console.error('[Board] 게시글 수정 실패:', error);
          alert(t('게시글 수정에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
        });
    }
  };

  // 댓글 저장 핸들러
  const handleSaveComment = () => {
    if (!commentData.content.trim()) {
      alert(t('댓글을 입력해주세요.'));
      return;
    }

    if (postDetail) {
      // API 응답 구조에 따라 게시글 ID 추출
      const post = postDetail.post || postDetail;
      const postId = post.id;

      (dispatch as any)(createComment({
        postId: postId,
        commentData: {
          content: commentData.content,
          edit_type: 'WYSIWYG'
        }
      })).then(() => {
        setCommentData({ content: '' });
        
        // 댓글 추가 후 게시글 상세 정보 다시 불러오기
        (dispatch as any)(fetchPostDetail(postId));
      });
    }
  };

  // 게시글 삭제 핸들러
  const handleDeletePost = () => {
    if (postDetail) {
      // API 응답 구조에 따라 게시글 ID 추출
      const post = postDetail.post || postDetail;
      const postId = post.id;
      
      (dispatch as any)(deletePost(postId))
        .unwrap()
        .then(() => {
          setShowDeleteModal(false);
          
          // 삭제 성공 메시지 표시
          alert(t('게시글이 성공적으로 삭제되었습니다.'));
          
          // 모드 변경
          setMode('list');
          
          // 게시글 목록 새로고침 - 삭제 후 최신 데이터 반영
          if (categoryId || selectedBoardId) {
            const boardId = categoryId || selectedBoardId;
            const numericBoardId = boardId ? Number(boardId) : null;
            
            if (numericBoardId) {

              if (fetchPostsOnce) {
                fetchPostsOnce(numericBoardId, currentPage, perPage);
              } else {
                (dispatch as any)(fetchPosts({
                  categoryId: numericBoardId,
                  page: currentPage,
                  perPage
                }));
              }
            }
          }
          
          // 웹빌더 미리보기 모드에서는 라우팅하지 않음
          if (isPreview) {
            return;
          }
          
          // 명시적으로 게시판 목록 페이지로 이동
          // URL에서 게시판 코드 추출 - 원래 경로 그대로 유지
          const pathSegments = location.pathname.split('/').filter(Boolean);
          const boardCodeFromPath = pathSegments.length > 0 ? pathSegments[0] : '';
          
          if (boardCodeFromPath) {
            navigate(`/${boardCodeFromPath}/:target`);
          } 
          
          // 모드 변경
          setMode('list');
        })
        .catch((error: any) => {
          console.error('[게시글 삭제] 실패:', error);
          alert(t('게시글 삭제에 실패했습니다: ') + (error.message || t('알 수 없는 오류')));
        });
    }
  };

  // 페이지 이동 핸들러
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);
  
  // 게시글 목록 페이지로 이동하는 함수
  const navigateToList = (boardCodeParam?: any) => {
    // 모드 직접 변경
    setMode('list');
    setPostId(null);
    setFormData({ title: '', content: '', originalBlock: null });
    
    // 게시글 목록 새로고침 - 상세보기나 작성에서 돌아올 때 최신 데이터 반영
    if (categoryId || selectedBoardId) {
      const boardId = categoryId || selectedBoardId;
      const numericBoardId = boardId ? Number(boardId) : null;
      
      if (numericBoardId) {

        if (fetchPostsOnce) {
          fetchPostsOnce(numericBoardId, currentPage, perPage);
        } else {
          (dispatch as any)(fetchPosts({
            categoryId: numericBoardId,
            page: currentPage,
            perPage
          }));
        }
      }
    }
    
    // 웹빌더 미리보기 모드에서는 실제 navigate 하지 않음
    if (isPreview) {

      return;
    }
    
    try {
      // boardCodeParam이 객체인지 확인하고 적절히 처리
      let boardCodeValue;
      
      if (typeof boardCodeParam === 'object' && boardCodeParam !== null) {
        // 객체에서 code 또는 id 필드 추출
        if (boardCodeParam.code) {
          boardCodeValue = boardCodeParam.code;
        } else if (boardCodeParam.id) {
          // ID로 게시판 찾기
          const boardWithId = boards.find((b: any) => b.id === boardCodeParam.id);
          boardCodeValue = boardWithId ? boardWithId.code : '';
        } else {
          // 객체지만 code나 id가 없는 경우 현재 경로에서 추출하거나 기존 boardCode 사용
          const pathSegments = location.pathname.split('/').filter(Boolean);
          boardCodeValue = pathSegments.length > 0 ? pathSegments[0] : (boardCode || '');
        }
      } else {
        // 문자열이나 다른 타입이면 그대로 사용
        boardCodeValue = boardCodeParam || boardCode || '';
      }
      
      // 명시적으로 문자열로 변환 (이미 문자열이 아닌 경우에만)
      const targetBoardCode = boardCodeValue.toString();
      
      // finapp 모드인지 확인
      const isFinapp = location.pathname.includes('/finapp/m/') || location.pathname.includes('/gpc/m/');
      
      if (isFinapp) {
        // finapp 모드에서는 현재 경로에서 마지막 부분만 제거
        const pathParts = location.pathname.split('/');
        // 마지막 부분이 숫자인 경우(상세보기) 제거
        if (pathParts.length > 0 && !isNaN(Number(pathParts[pathParts.length - 1]))) {
          pathParts.pop();
        }
        const targetPath = pathParts.join('/');
        navigate(targetPath);
      } else if (!targetBoardCode) {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        if (pathSegments.length > 0) {
          const currentBoardCode = pathSegments[0] + pathSegments[1];
          const targetPath = `/${currentBoardCode}/:target`;
          navigate(targetPath);
        } else {
          // 경로도 없으면 루트로 이동
          navigate('/');
        }
      } else {
        // 목록 경로 - 사용자 입력 그대로 사용
        const targetPath = `/${targetBoardCode}/:target`;
        navigate(targetPath);
      }
      
      // 상태만 변경하고 API 호출은 useEffect에서 처리하도록 수정
      // 이미 selectedBoardId가 설정되어 있으므로 useEffect가 자동으로 호출함
      
      setCurrentPage(1);
      
    } catch (error) {
      console.error('[네비게이션] 목록 화면 이동 중 오류 발생:', error);
      
      // 오류 발생 시 기본 경로로 이동 시도
      navigate('/');
    }
  };

  // 새 게시글 작성 페이지로 이동하는 함수
  const navigateToNew = (boardCodeParam: any) => {

    
    // 모드 직접 변경 (navigate 전에 모드부터 변경)
    setMode('new');
    setPostId(null);
    setFormData({ title: '', content: '', originalBlock: null });
    
    // 게시판 ID 설정 - boardCodeParam으로 게시판 찾기
    if (boardCodeParam && boards && boards.length > 0) {
      const foundBoard = boards.find((board: any) => board.code === boardCodeParam);
      if (foundBoard) {

        setSelectedBoardId(foundBoard.id);
      }
    }
    
    // 웹빌더 미리보기 모드에서는 실제 navigate 하지 않음
    if (isPreview) {

      return;
    }
    
    try {
      // finapp 모드인지 확인
      const isFinapp = location.pathname.includes('/finapp/m/') || location.pathname.includes('/gpc/m/');
      
      if (isFinapp) {
        // finapp 모드에서는 현재 경로에 /new 추가
        const targetPath = `${location.pathname}/new`;
        navigate(targetPath, { replace: false });
      } else {
        // boardCodeParam 그대로 사용
        let boardCodeValue = boardCodeParam || boardCode;
        
        // 문자열로 변환만 하고 대소문자 변환 없음
        const targetBoardCode = String(boardCodeValue);
        
        // URL 생성 및 이동 - 사용자 입력 경로 유지, 대소문자 변환 없음
        const targetPath = `/${targetBoardCode}/new`;
        
        
        // navigate 직접 호출 (replace: false로 설정하여 히스토리에 추가)
        navigate(targetPath, { replace: false });
      }
      
    } catch (error) {
      console.error('[네비게이션] 새 게시글 작성 화면 이동 중 오류 발생:', error);
    }
  };

  // 게시글 상세 페이지로 이동하는 함수
  const navigateToDetail = (postIdParam: number | string, boardCodeParam?: any) => {
    try {
      if (!postIdParam) {
        console.error('[네비게이션] 게시글 ID가 없습니다. 이동이 취소됩니다.');
        return;
      }
      
      // 모드 직접 변경
      setMode('detail');
      setPostId(postIdParam);
      
      // 게시글 상세 정보와 댓글 함께 불러오기 - 객체 대신 직접 ID 전달
      (dispatch as any)(fetchPostDetail(postIdParam));
      
      // 웹빌더 미리보기 모드에서는 실제 navigate 하지 않음
      if (isPreview) {

        return;
      }
      
      // boardCodeParam이 객체인지 확인
      let boardCodeValue;
      
      if (typeof boardCodeParam === 'object' && boardCodeParam !== null) {
        // 객체에서 code 또는 id 필드 추출
        if (boardCodeParam.code) {
          boardCodeValue = boardCodeParam.code;
        } else if (boardCodeParam.id) {
          // ID로 게시판 찾기
          const boardWithId = boards.find((b: any) => b.id === boardCodeParam.id);
          boardCodeValue = boardWithId ? boardWithId.code : '';
        } else {
          boardCodeValue = '';
        }
      } else {
        // 문자열이나 다른 타입이면 그대로 사용
        boardCodeValue = boardCodeParam || '';
      }
      
      // 현재 경로에서 마지막 세그먼트 추출하고 게시글 ID로 대체
      const pathSegments = location.pathname.split('/').filter(Boolean);
      let targetPath;
      
      // finapp 모드인지 확인
      const isFinapp = location.pathname.includes('/finapp/m/') || location.pathname.includes('/gpc/m/');
      
      if (isFinapp) {
        // finapp 모드에서는 /finapp/m/notice/123 형태로 만들기
        // 현재 경로에서 마지막 부분만 게시글 ID로 교체
        const currentPath = location.pathname;
        // 마지막 슬래시 이후를 제거하고 새로운 ID 추가
        const lastSlashIndex = currentPath.lastIndexOf('/');
        const basePath = lastSlashIndex > 0 ? currentPath : currentPath;
        targetPath = `${basePath}/${postIdParam}`;
      } else if (pathSegments.length >= 1) {
        // 일반 모드에서는 기존 로직 사용
        const boardCodeFromPath = pathSegments[0];
        targetPath = `/${boardCodeFromPath}/${postIdParam}`;
      } else if (boardCodeValue) {
        // boardCodeParam에서 추출한 값 사용
        targetPath = `/${boardCodeValue}/${postIdParam}`;
      } else if (boardCode) {
        // 경로가 비어있거나 없는 경우 boardCode 사용
        targetPath = `/${boardCode}/${postIdParam}`;
      } else {
        // 모든 값이 없는 경우 오류 로그
        console.error('[네비게이션] 게시판 코드가 없어 이동할 수 없습니다.');
        return;
      }
      

      navigate(targetPath, { replace: false });
      
    } catch (error) {
      console.error('[네비게이션] 게시글 상세 화면 이동 중 오류 발생:', error);
    }
  };

  // 게시글 수정 모드로 전환 핸들러
  const handleGoToEdit = () => {
    if (!postDetail) {
      console.log('[Board] handleGoToEdit: postDetail이 없습니다');
      return;
    }
    
    // API 응답 구조에 따라 게시글 데이터 추출
    const post = postDetail.post || postDetail;
    console.log('[Board] handleGoToEdit 실행:', {
      postDetail,
      post,
      title: post.title,
      content: post.content?.substring(0, 100) + '...',
      originalBlock: post.originalBlock,
      hasOriginalBlock: !!post.originalBlock
    });
    
    setFormData({
      title: post.title,
      content: post.content,
      originalBlock: post.originalBlock || null
    });
    setMode('edit');
  };

  // 취소 핸들러 - 새 글 작성 취소
  const handleCancelNew = () => {

    // 폼 데이터 초기화
    setFormData({ title: '', content: '', originalBlock: null });
    // 목록으로 이동
    if (isPreview) {
      setMode('list');
      // 게시글 목록 새로고침
      if (categoryId || selectedBoardId) {
        const boardId = categoryId || selectedBoardId;
        const numericBoardId = boardId ? Number(boardId) : null;
        
        if (numericBoardId) {

          if (fetchPostsOnce) {
            fetchPostsOnce(numericBoardId, currentPage, perPage);
          } else {
            (dispatch as any)(fetchPosts({
              categoryId: numericBoardId,
              page: currentPage,
              perPage
            }));
          }
        }
      }
    } else {
      navigateToList();
    }
  };

  // 취소 핸들러 - 수정 취소
  const handleCancelEdit = () => {

    // 폼 데이터 초기화
    setFormData({ title: '', content: '', originalBlock: null });
    // 상세보기로 이동
    if (postDetail) {
      setMode('detail');
      // 이미 postDetail이 있으므로 다시 로드할 필요 없음
    }
  };

  // 현재 게시판 이름 가져오는 함수
  const getBoardTitle = () => {
    // 속성에서 설정된 제목만 사용 (데이터베이스 값은 사용하지 않음)
    const title = getDeviceValue('title', '');
    
    // 제목이 있을 때만 다국어 처리 적용
    return title ? t(title) : '';
  };

  // URL 경로를 분석하여 필요한 정보를 추출하는 useEffect
  useEffect(() => {
    // 웹빌더 미리보기 모드에서는 URL 파싱하지 않음
    if (isPreview) {

      return;
    }
    
    // 이미 파싱 중이면 중복 실행 방지
    if (urlParsingRef.current) {
      return;
    }
    
    // 파싱 시작 표시
    urlParsingRef.current = true;
    
    try {
      // parameter 값이 있어도 URL 파싱은 항상 수행 (라우팅과 parameter는 별개)
      
      // 루트 경로인 경우 무시
      if (!location.pathname || location.pathname === '/') {
        urlParsingRef.current = false;
        return;
      }
      
      // 경로 세그먼트 추출 (첫 번째 '/'를 제거)
      const segments = location.pathname.split('/').filter(segment => segment);
      
      // 세그먼트가 존재하지 않으면 무시
      if (!segments || segments.length === 0) {
        urlParsingRef.current = false;
        return;
      }
      
      // finapp 모드인지 확인
      const isFinapp = location.pathname.includes('/finapp/m/') || location.pathname.includes('/gpc/m/');
      let extractedBoardCode: string;
      let relevantSegments: string[];
      
      if (isFinapp) {
        // finapp 모드에서는 /finapp/m/ 이후의 세그먼트만 고려
        const finappIndex = segments.indexOf('m');
        if (finappIndex >= 0 && finappIndex < segments.length - 1) {
          extractedBoardCode = segments[finappIndex + 1];
          relevantSegments = segments.slice(finappIndex + 1);
        } else {
          urlParsingRef.current = false;
          return;
        }
      } else {
        // 일반 모드
        extractedBoardCode = segments[0];
        relevantSegments = segments;
      }
      
      // 게시판 코드 설정 (목록 의존성 없이)
      setBoardCode(extractedBoardCode);
      
      // 두 번째 세그먼트 분석 (relevantSegments 사용)
      if (relevantSegments.length === 1) {
        // URL 패턴: /:target 또는 /finapp/m/:target
        setMode('list');
        setPostId(null);
        setFormData({ title: '', content: '', originalBlock: null });
        
        // 게시판 목록이 로드되었고 URL로 찾은 게시판과 parameter로 지정된 게시판이 다른 경우에만
        // selectedBoardId 설정과 게시글 로드를 실행
        if (boards && boards.length > 0) {
          const foundBoard = boards.find((board: any) => board.code === extractedBoardCode);
          
          if (foundBoard && (!selectedBoardId || foundBoard.id !== selectedBoardId)) {
            setSelectedBoardId(foundBoard.id);
            setCurrentPage(1);
          }
        }
      } else if (relevantSegments.length === 2) {
        if (relevantSegments[1] === 'new') {
          // URL 패턴: /:target/new 또는 /finapp/m/:target/new

          setMode('new');
          setPostId(null);
          setFormData({ title: '', content: '', originalBlock: null });
          
          // 게시판 목록이 로드되었고 URL로 찾은 게시판 코드가 있는 경우
          if (boards && boards.length > 0 && extractedBoardCode) {
            const foundBoard = boards.find((board: any) => board.code === extractedBoardCode);
            if (foundBoard) {

              setSelectedBoardId(foundBoard.id);
            }
          }
        } else if (!isNaN(Number(relevantSegments[1]))) {
          // URL 패턴: /:target/:postId 또는 /finapp/m/:target/:postId
          const viewPostId = relevantSegments[1];
          setMode('detail');
          setPostId(viewPostId);
          
          // 게시글 상세 정보는 mode가 변경된 후 다른 useEffect에서 처리
        } else {
          console.error(`[URL 파싱] 두 번째 세그먼트 ${relevantSegments[1]}가 숫자나 'new'가 아니므로 처리할 수 없습니다.`);
        }
      } else {
        // 알 수 없는 URL 패턴
        console.warn('[URL 파싱] 알 수 없는 URL 패턴입니다:', location.pathname);
      }
    } catch (error) {
      console.error('[URL 파싱] URL 처리 중 오류가 발생했습니다:', error);
    } finally {
      // 파싱 완료 표시
      urlParsingRef.current = false;
    }
  }, [location.pathname]); // 경로 변경 시에만 실행

  // 게시판 ID 설정 - 의존성 배열 최소화하여 중복 실행 방지
  useEffect(() => {
    // 이미 선택된 ID가 있거나 boards가 없으면 건너뜀
    if (selectedBoardId || !boards || boards.length === 0) {
      return;
    }
    
    // parameter 값이 있으면 그대로 사용
    if (categoryId) {
      // 이미 초기 useEffect에서 설정했다면 중복 설정 방지
      if (!selectedBoardId) {
        setSelectedBoardId(categoryId);
        // boards가 있으면 boardCode도 설정
        const foundBoard = boards.find((board: any) => board.id === categoryId);
        if (foundBoard) {
          setBoardCode(foundBoard.code);
        }
      }
      return;
    }
    
    // URL에서 게시판 코드 추출 - 최초 1회만 실행
    if (boardCode && !selectedBoardId) {
      const foundBoard = boards.find((board: any) => board.code === boardCode);
      
      if (foundBoard) {
        setSelectedBoardId(foundBoard.id);
      } else if (boards.length > 0) {
        // URL로 게시판을 찾지 못했을 때 첫 번째 게시판 사용
        setSelectedBoardId(boards[0].id);
        setBoardCode(boards[0].code);
      }
    }
  }, [boards, categoryId, selectedBoardId, boardCode]);

  // 모드 변경 시 효과 추적
  useEffect(() => {
    // new 모드일 때 게시판 ID 설정 (renderNewMode에서 수행하던 작업을 여기로 이동)
    if (mode === 'new') {

      
      // URL 경로와 target 파라미터 확인
      const urlPathSegments = location.pathname.split('/').filter(Boolean);
      const urlPath = urlPathSegments.length > 0 ? urlPathSegments[0] : '';
      
      // parameter 속성이 있으면 우선 사용
      if (categoryId) {

        setSelectedBoardId(categoryId);
      } 
      // boardCode가 있고 boards가 로드되었을 때
      else if (boardCode && boards && boards.length > 0 && !selectedBoardId) {
        const foundBoard = boards.find((board: any) => board.code === boardCode);
        if (foundBoard) {

          setSelectedBoardId(foundBoard.id);
        }
      }
      // URL 기반 처리
      else if (!selectedBoardId && boards && boards.length > 0) {
        const routeTarget = urlPath || target || boardParameter;
        
        if (routeTarget) {
          const foundBoard = boards.find((board: any) => 
            board.code === routeTarget || 
            board.code === String(routeTarget)
          );
          
          if (foundBoard) {

            setSelectedBoardId(foundBoard.id);
          }
        }
      }
    }
  }, [mode, location.pathname, categoryId, selectedBoardId, boards, target, boardParameter, boardCode]);

  // currentPage 변경 시 게시글 다시 로드 (페이징 처리)
  useEffect(() => {
    // 초기 렌더링이 아닐 때만 페이지 변경 시 게시글 목록 다시 로드
    if (!initialRenderRef.current && mode === 'list') {
      // 현재 선택된 게시판이 있는 경우에만 API 호출
      if (categoryId || selectedBoardId) {
        // 명시적으로 number 타입으로 변환
        const boardId = categoryId || selectedBoardId;
        const numericBoardId = boardId ? Number(boardId) : null;
        
        if (numericBoardId) {
          if (fetchPostsOnce) {
            fetchPostsOnce(numericBoardId, currentPage, perPage);
          } else {
            (dispatch as any)(fetchPosts({
              categoryId: numericBoardId,
              page: currentPage,
              perPage
            }));
          }
        }
      }
    }
  }, [currentPage]); // currentPage 변경 시에만 실행

  // 여백 설정 초기화 및 변경 감지를 위한 효과 - 제거됨
  // 스타일은 computedStyle에서 처리하므로 별도 useEffect 불필요

  // DOM 직접 조작 제거 - React 스타일로 처리

  // 초기 렌더링 완료 처리
  useEffect(() => {
    initialRenderRef.current = false;
  }, []);

  // 이전 모드를 추적하기 위한 ref
  const prevModeRef = useRef<string>('');
  
  // 모드 변경에 따른 데이터 로드
  useEffect(() => {
    const prevMode = prevModeRef.current;
    const isFromDetailOrNew = prevMode === 'detail' || prevMode === 'new' || prevMode === 'edit';
    
    // detail 모드일 때 게시글 상세 정보 로드
    if (mode === 'detail' && postId && !initialRenderRef.current) {
      (dispatch as any)(fetchPostDetail(postId));
    }
    // list 모드일 때 게시글 목록 로드
    else if (mode === 'list' && selectedBoardId && !initialRenderRef.current) {
      // 상세보기나 작성 모드에서 돌아온 경우 항상 새로고침
      if (isFromDetailOrNew) {

        if (fetchPostsOnce) {
          fetchPostsOnce(Number(selectedBoardId), currentPage, perPage);
        } else {
          (dispatch as any)(fetchPosts({
            categoryId: Number(selectedBoardId),
            page: currentPage,
            perPage
          }));
        }
      }
      // categoryId가 없는 경우에만 추가 로드 (기존 로직)
      else if (!categoryId) {
        if (fetchPostsOnce) {
          fetchPostsOnce(Number(selectedBoardId), currentPage, perPage);
        } else {
          (dispatch as any)(fetchPosts({
            categoryId: Number(selectedBoardId),
            page: currentPage,
            perPage
          }));
        }
      }
    }
    
    // 현재 모드를 이전 모드로 저장
    prevModeRef.current = mode;
  }, [mode, postId, selectedBoardId]);

 

  // 게시판 목록 페이지 렌더링
  const renderListMode = () => {

    
    return (
      <div className="board-list-mode">
        <div className="board-top-buttons">
          {getBoardTitle() ? (
            <Typography variant="h4" component="h1" className="board-title">
              {getBoardTitle()}
            </Typography>
          ) : (
            <div></div>
          )}
          
          {currentUser && getDeviceValue('showNewButton', true) && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => navigateToNew(boardCode)}
              className="board-new-button"
            >
              {t('새 글쓰기')}
            </Button>
          )}
        </div>
        
        {postsLoading ? (
          <div className="board-loading">
            <div className="board-loading-spinner"></div>
            <div className="board-loading-text">{t('게시글을 불러오는 중...')}</div>
          </div>
        ) : (
          <>
            {posts && posts.length > 0 ? (
              <div className="board-list-table-container">
                <table className="board-list-table">
                  <thead className="board-list-thead">
                    <tr>
                      <th className="board-list-th board-list-th-id">{t('번호')}</th>
                      <th className="board-list-th board-list-th-title">{t('제목')}</th>
                      <th className="board-list-th board-list-th-author">{t('작성자')}</th>
                      <th className="board-list-th board-list-th-date">{t('작성일')}</th>
                      <th className="board-list-th board-list-th-views">{t('조회')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post: Post) => (
                      <tr 
                        key={post.id} 
                        onClick={() => navigateToDetail(post.id)}
                        className="board-list-tr"
                      >
                        <td className="board-list-td board-list-td-id">{post.id}</td>
                        <td className="board-list-td board-list-td-title">
                          <div className="board-list-td-title-text">{post.title}</div>
                        </td>
                        <td className="board-list-td board-list-td-author">
                          {post.administratorId ? t('관리자') : 
                           (currentUser && post.userId && 
                            ((currentUser.id && currentUser.id.toString() === post.userId.toString()) || 
                             (currentUser.userId && currentUser.userId.toString() === post.userId.toString()))) ? t('나') : 
                           (post.user?.name || post.author || t('익명'))}
                        </td>
                        <td className="board-list-td board-list-td-date">{new Date(post.createdAt).toLocaleDateString()}</td>
                        <td className="board-list-td board-list-td-views">{post.views || post.viewCount || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="board-empty-list">
                {t('게시글이 없습니다.')}
              </div>
            )}

            {pagination && pagination.totalPages > 1 && (
              <div className="board-pagination">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`board-pagination-button ${page === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // 게시글 상세 페이지 렌더링
  const renderDetailMode = () => {

    
    // 로딩 중 화면 표시
    if (postDetailLoading) {
      return (
        <div className="board-loading">
          <div className="board-loading-spinner"></div>
          <div className="board-loading-text">{t('게시글을 불러오는 중...')}</div>
        </div>
      );
    }

    // 게시글이 없는 경우 처리
    if (!postDetail) {
      return (
        <div className="board-empty-list">
          <Typography variant="h5" color="error">
            {t('게시글을 찾을 수 없습니다.')}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<ListIcon />} 
            onClick={() => navigateToList()}
            className="board-list-button"
            style={{ marginTop: '20px' }}
          >
            {t('목록으로 돌아가기')}
          </Button>
        </div>
      );
    }

    // API 응답 구조에 따라 게시글 데이터 추출
    const post = postDetail.post || postDetail;
    const comments = post.children || [];
    
    // 현재 사용자가 게시글 작성자인지 확인
    const isAuthor = 
      currentUser && 
      post.userId && 
      ((currentUser.id && currentUser.id.toString() === post.userId.toString()) ||
      (currentUser.userId && currentUser.userId.toString() === post.userId.toString()));
    
    
    return (
      <div className="board-detail-mode">
        <div className="board-detail-actions">
          <button 
            className="board-list-button"
            onClick={() => navigateToList()}
          >
            <ListIcon style={{ fontSize: '1rem', marginRight: '4px' }} />
            {t('목록')}
          </button>
          
          {/* 작성자일 때만 버튼 영역 표시 - 버튼별 표시 여부는 각각 개별 조건으로 처리 */}
          {isAuthor && (
            <div className="board-action-buttons">
              {/* 수정하기 버튼 - showEditButton이 명시적으로 false가 아니면 표시 */}
              <button 
                className="board-edit-button"
                onClick={handleGoToEdit}
                style={{display: getDeviceValue('showEditButton', true) ? 'inline-block' : 'none'}}
              >
                {t('수정')}
              </button>
              
              {/* 삭제하기 버튼 - showDeleteButton이 명시적으로 false가 아니면 표시 */}
              <button 
                className="board-delete-button"
                onClick={() => setShowDeleteModal(true)}
                style={{display: getDeviceValue('showDeleteButton', true) ? 'inline-block' : 'none'}}
              >
                {t('삭제')}
              </button>
            </div>
          )}
        </div>
        
        <div className="board-detail-container">
          <div className="board-detail-header">
            <h2 className="board-detail-title">{post.title}</h2>
            
            <div className="board-detail-info">
              <div className="board-detail-info-item">
                <span className="board-detail-info-label">{t('작성자')}:</span>
                {post.administratorId ? t('관리자') : 
                 (currentUser && post.userId && 
                  ((currentUser.id && currentUser.id.toString() === post.userId.toString()) || 
                   (currentUser.userId && currentUser.userId.toString() === post.userId.toString()))) ? t('나') : 
                 (post.user?.name || post.author || t('익명'))}
              </div>
              <div className="board-detail-info-item">
                <span className="board-detail-info-label">{t('작성일')}:</span>
                {new Date(post.createdAt).toLocaleDateString()}
              </div>
              <div className="board-detail-info-item">
                <span className="board-detail-info-label">{t('조회수')}:</span>
                {post.views || post.viewCount || 0}
              </div>
            </div>
          </div>
          
          <div className="board-detail-content">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </div>
        
        <div className="board-comments-section">
          <h3 className="board-comments-title">
            {t('댓글')} ({comments.length})
          </h3>
          
          {comments.length > 0 ? (
            <div className="board-comments-list">
              {comments.map((comment: any) => (
                <div key={comment.id} className="board-comment-item">
                  <div className="board-comment-header">
                    <span className="board-comment-author">
                      {comment.administratorId ? t('관리자') : 
                       (currentUser && comment.userId && 
                        ((currentUser.id && currentUser.id.toString() === comment.userId.toString()) || 
                         (currentUser.userId && currentUser.userId.toString() === comment.userId.toString()))) ? t('나') : 
                       (comment.user?.name || comment.author || t('익명'))}
                    </span>
                    <span className="board-comment-date">{new Date(comment.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div 
                    className="board-comment-content"
                    dangerouslySetInnerHTML={{ __html: comment.content }} 
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="board-no-comments">{t('아직 댓글이 없습니다.')}</div>
          )}
          
          {currentUser && getDeviceValue('showCommentButton', true) && (
            <div className="board-comment-form">
              <div className="board-comment-form-inner">
                {/* TODO: 향후 리치 에디터로 변경 가능
                <div className="board-comment-editor">
                  <HtmlEditor
                    initialData={commentData.content}
                    onChange={(content) => setCommentData(prev => ({ ...prev, content }))}
                    placeholder={t('댓글을 입력하세요...')}
                  />
                </div>
                */}
                <textarea
                  placeholder={t('댓글을 입력하세요...')}
                  value={commentData.content}
                  onChange={handleCommentChange}
                  className="board-comment-textarea"
                />
                <div className="board-comment-submit">
                  <button 
                    className="board-comment-button"
                    onClick={handleSaveComment}
                  >
                    {t('댓글 작성')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {showDeleteModal && (
          <div className="board-delete-modal-overlay">
            <div className="board-delete-modal">
              <h3 className="board-delete-modal-title">{t('게시글을 삭제하시겠습니까?')}</h3>
              <p className="board-delete-modal-text">{t('삭제한 게시글은 복구할 수 없습니다.')}</p>
              <div className="board-delete-modal-actions">
                <button 
                  className="board-delete-modal-cancel"
                  onClick={() => setShowDeleteModal(false)}
                >
                  {t('취소')}
                </button>
                <button 
                  className="board-delete-modal-confirm"
                  onClick={handleDeletePost}
                >
                  {t('삭제')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 게시글 작성 페이지 렌더링
  const renderNewMode = () => {
    return (
      <div className="board-form-mode">
        <div className="board-form-container">
          <div className="board-form-header">
            <h2 className="board-form-title">
              {getBoardTitle() ? `${getBoardTitle()} - ${t('새 글 작성')}` : t('새 글 작성')}
            </h2>
          </div>
          
          <div className="board-form-content">
            <div className="board-form-group">
              <label htmlFor="title" className="board-form-label">{t('제목')}</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="board-form-input"
                placeholder={t('제목을 입력하세요')}
              />
            </div>
            
            <div className="board-form-group">
              <label htmlFor="content" className="board-form-label">{t('내용')}</label>
              <div className="board-form-editor">
                <YooptaHtmlEditor
                  initialData={editorInitialData}
                  onChange={handleEditorChange}
                  placeholder={t('내용을 입력하세요')}
                  editorKey={`board-new-${categoryId || selectedBoardId || 'default'}`}
                  key={`board-new-editor`}
                />
              </div>
            </div>
            
            <div className="board-form-actions">
              <button 
                className="board-form-cancel"
                onClick={handleCancelNew}
              >
                {t('취소')}
              </button>
              <button 
                className="board-form-submit"
                onClick={handleSavePost}
              >
                {t('저장')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 게시글 수정 페이지 렌더링
  const renderEditMode = () => {
    return (
      <div className="board-form-mode">
        <div className="board-form-container">
          <div className="board-form-header">
            <h2 className="board-form-title">
              {getBoardTitle() ? `${getBoardTitle()} - ${t('게시글 수정')}` : t('게시글 수정')}
            </h2>
          </div>
          
          <div className="board-form-content">
            <div className="board-form-group">
              <label htmlFor="title" className="board-form-label">{t('제목')}</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="board-form-input"
                placeholder={t('제목을 입력하세요')}
              />
            </div>
            
            <div className="board-form-group">
              <label htmlFor="content" className="board-form-label">{t('내용')}</label>
              <div className="board-form-editor">
                <YooptaHtmlEditor
                  initialData={editorInitialData}
                  onChange={handleEditorChange}
                  placeholder={t('내용을 입력하세요')}
                  editorKey={`board-edit-${postId || categoryId || selectedBoardId || 'default'}`}
                  key={`board-edit-${postId || 'edit'}`}
                />
              </div>
            </div>
            
            <div className="board-form-actions">
              <button 
                className="board-form-cancel"
                onClick={handleCancelEdit}
              >
                {t('취소')}
              </button>
              <button 
                className="board-form-submit"
                onClick={handleSavePost}
              >
                {t('저장')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 상황에 맞는 컴포넌트 렌더링
  const renderContent = () => {
    // 모드에 따라 다른 컴포넌트 렌더링
    switch (mode) {
      case 'detail':
        return renderDetailMode();
      case 'new':
        return renderNewMode();
      case 'edit':
        return renderEditMode();
      case 'list':
      default:
        return renderListMode();
          }
    };
  
    // finapp 모드 확인
    const isFinappMode = location.pathname.includes('/finapp/') || location.pathname.includes('/gpc/');
  
    // 최종 렌더링
    return (
      <section 
        className={`board-component-container ${isFinappMode ? 'finapp-mode' : ''}`}
        data-device={currentDevice}
        data-finapp={isFinappMode ? 'true' : 'false'}
        style={computedStyle}
      >
        {renderContent()}
      </section>
    );
  };

// API 호출 추적을 위한 전역 변수
const apiCallTracker = {
  boards: false,
  posts: new Map<string, boolean>()
};

// 중복 호출 방지를 위한 HOC
const BoardComponentWithTracking: React.FC<any> = (props) => {
  const dispatch = useDispatch();
  
  // fetchBoards 래퍼
  const fetchBoardsOnce = useCallback(() => {
    if (!apiCallTracker.boards) {

      (dispatch as any)(fetchBoards());
      apiCallTracker.boards = true;
    }
  }, [dispatch]);
  
  // fetchPosts 래퍼
  const fetchPostsOnce = useCallback((categoryId: number, page: number, perPage: number) => {
    const key = `${categoryId}-${page}`;
    if (!apiCallTracker.posts.get(key)) {

      (dispatch as any)(fetchPosts({ categoryId, page, perPage }));
      apiCallTracker.posts.set(key, true);
      
      // 5초 후 캐시 제거 (새로고침 허용)
      setTimeout(() => {
        apiCallTracker.posts.delete(key);
      }, 5000);
    } else {

    }
  }, [dispatch]);
  
  // props에 추가하여 전달
  const enhancedProps = {
    ...props,
    fetchBoardsOnce,
    fetchPostsOnce
  };
  
  return <BoardComponent {...enhancedProps} />;
};

export default React.memo(BoardComponentWithTracking, (prevProps, nextProps) => {
  // props가 실제로 변경되었을 때만 리렌더링
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
}); 